import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  GitBranch,
  Play,
  Flag,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Loader2,
  Target,
  Link2,
  Wand2,
  Sparkles,
  Image as ImageIcon
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { getAuthHeaders } from '../../services/adminApi';

import { ImagePicker } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Outcome types with colors
const OUTCOME_TYPES = [
  { value: 'optimal', label: 'Optimal', color: 'bg-green-500', description: '100% bonus' },
  { value: 'good', label: 'Good', color: 'bg-blue-500', description: '75% bonus' },
  { value: 'neutral', label: 'Neutral', color: 'bg-gray-500', description: '50% bonus' },
  { value: 'poor', label: 'Poor', color: 'bg-orange-500', description: '25% bonus' },
  { value: 'failure', label: 'Failure', color: 'bg-red-500', description: '0% bonus' },
];

// Generate UUID
const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

// Helper: Find parent nodes (nodes whose choices lead to a given node)
const findParentNodes = (nodeId, allNodes) => {
  return allNodes.filter(n =>
    n.choices?.some(c => c.target_node_id === nodeId)
  );
};

// ============================================
// Node Editor Modal
// ============================================
const NodeEditor = ({ node, allNodes, onSave, onCancel, onDelete, scenarioId }) => {
  const [formData, setFormData] = useState({
    title: node?.title || '',
    situation_text: node?.situation_text || '',
    image_url: node?.image_url || '',
    image_requirements: node?.image_requirements || '',
    is_start: node?.is_start || false,
    is_ending: node?.is_ending || false,
    ending_type: node?.ending_type || 'neutral',
    ending_message: node?.ending_message || '',
    choices: node?.choices || []
  });
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [imageError, setImageError] = useState(null);

  // Reference image settings for style consistency
  const [useReferenceImage, setUseReferenceImage] = useState(false);
  const [referenceStrength, setReferenceStrength] = useState(0.6);

  // Parent context for story progression
  const [useParentContext, setUseParentContext] = useState(true);

  // Find start node for reference image
  const startNode = allNodes.find(n => n.is_start);
  const referenceImageUrl = startNode?.image_url;
  const isStartNode = node?.is_start || false;
  const canUseReference = !isStartNode && referenceImageUrl;

  // Find parent nodes for context
  const parentNodes = node?.id ? findParentNodes(node.id, allNodes) : [];
  const hasParentContext = !isStartNode && parentNodes.length > 0;

  const handleGenerateImage = async () => {
    if (!node?.id || !scenarioId) {
      setImageError('Please save the node first before generating an image');
      return;
    }

    setGeneratingImage(true);
    setImageError(null);
    setGeneratedPrompt(null);

    try {
      // Build request body with optional reference image
      const requestBody = {
        required_elements: formData.image_requirements || null
      };

      // Add reference image if enabled and available
      if (useReferenceImage && canUseReference && referenceImageUrl) {
        requestBody.reference_image_url = referenceImageUrl;
        requestBody.reference_strength = referenceStrength;
      }

      // Add parent context if enabled and available
      if (useParentContext && hasParentContext) {
        const parentContextParts = parentNodes.map(p => {
          // Prefer generated_prompt if available, fall back to situation_text
          const text = p.generated_prompt || p.situation_text;
          const title = p.title || 'Previous Scene';
          return `[${title}]: ${text}`;
        });
        requestBody.parent_context = parentContextParts.join('\n\n');
      }

      // Start the async image generation
      const response = await fetch(
        `${API_BASE}/api/admin/scenarios/${scenarioId}/nodes/${node.id}/generate-image`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        setImageError(error.detail || 'Failed to start image generation');
        setGeneratingImage(false);
        return;
      }

      const data = await response.json();
      setGeneratedPrompt(data.generated_prompt);

      // If already completed (shouldn't happen, but handle it)
      if (data.status === 'completed' && data.image_url) {
        setFormData(prev => ({ ...prev, image_url: data.image_url }));
        setGeneratingImage(false);
        return;
      }

      // Poll for completion
      const jobId = data.job_id;
      const pollInterval = 2000; // 2 seconds
      const maxPolls = 90; // 3 minutes max (90 * 2 seconds)
      let polls = 0;

      const pollStatus = async () => {
        try {
          const statusResponse = await fetch(
            `${API_BASE}/api/admin/generate-image-status/${jobId}`,
            { headers: getAuthHeaders() }
          );

          if (!statusResponse.ok) {
            const error = await statusResponse.json();
            setImageError(error.detail || 'Failed to check generation status');
            setGeneratingImage(false);
            return;
          }

          const statusData = await statusResponse.json();

          if (statusData.status === 'completed') {
            setFormData(prev => ({ ...prev, image_url: statusData.image_url }));
            setGeneratedPrompt(statusData.generated_prompt);
            setGeneratingImage(false);
          } else if (statusData.status === 'error') {
            setImageError(statusData.error || 'Image generation failed');
            setGeneratingImage(false);
          } else if (statusData.status === 'processing') {
            polls++;
            if (polls < maxPolls) {
              setTimeout(pollStatus, pollInterval);
            } else {
              setImageError('Image generation timed out. Please try again.');
              setGeneratingImage(false);
            }
          }
        } catch (err) {
          setImageError('Network error checking generation status');
          setGeneratingImage(false);
        }
      };

      // Start polling
      setTimeout(pollStatus, pollInterval);

    } catch (err) {
      setImageError('Network error generating image');
      setGeneratingImage(false);
    }
  };

  const addChoice = () => {
    setFormData({
      ...formData,
      choices: [
        ...formData.choices,
        {
          id: generateId(),
          text: '',
          target_node_id: '',
          feedback: '',
          points_modifier: 0,
          is_optimal: false
        }
      ]
    });
  };

  const updateChoice = (index, field, value) => {
    const updated = [...formData.choices];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, choices: updated });
  };

  const removeChoice = (index) => {
    setFormData({
      ...formData,
      choices: formData.choices.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Available target nodes (excluding this node)
  const availableTargets = allNodes.filter(n => n.id !== node?.id);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Title */}
      <div>
        <Label htmlFor="title">Scene Title (optional)</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., The Dark Alley"
        />
      </div>

      {/* Situation Text */}
      <div>
        <Label htmlFor="situation_text">Situation Text *</Label>
        <textarea
          id="situation_text"
          className="w-full px-3 py-2 border rounded-md text-sm min-h-[100px]"
          value={formData.situation_text}
          onChange={(e) => setFormData({ ...formData, situation_text: e.target.value })}
          placeholder="Describe the situation the student faces..."
          required
        />
      </div>

      {/* Scene Image */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Scene Image (optional)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGenerateImage}
            disabled={generatingImage || !node?.id}
            title={!node?.id ? 'Save the node first to generate an image' : 'Generate AI image'}
          >
            {generatingImage ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            <span className="ml-1">AI Generate</span>
          </Button>
        </div>

        <ImagePicker
          value={formData.image_url}
          onChange={(url) => setFormData({ ...formData, image_url: url })}
          context="scenarios"
          previewSize="md"
          allowClear={true}
        />

        {/* Generation status */}
        {generatingImage && (
          <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 p-2 rounded">
            <Sparkles className="h-4 w-4 animate-pulse" />
            Generating AI image... This may take 10-30 seconds.
          </div>
        )}

        {/* Generated prompt */}
        {generatedPrompt && (
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
            <p className="font-medium mb-1">Generated prompt:</p>
            <p className="italic">{generatedPrompt}</p>
          </div>
        )}

        {/* Error message */}
        {imageError && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 p-2 rounded flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {imageError}
          </div>
        )}

        {/* Image Requirements */}
        <div className="mt-3">
          <Label htmlFor="image_requirements">Required Image Elements (optional)</Label>
          <textarea
            id="image_requirements"
            className="w-full px-3 py-2 border rounded-md text-sm mt-1"
            rows={2}
            value={formData.image_requirements}
            onChange={(e) => setFormData({ ...formData, image_requirements: e.target.value })}
            placeholder="e.g., Include a sensei with a black belt, student wearing a white gi, dojo with wooden floors..."
          />
          <p className="text-xs text-gray-500 mt-1">
            Specify elements that must appear in the generated image. These will be marked as required in the prompt.
          </p>
        </div>

        {/* Reference Image Settings - Only show for non-start nodes when start node has an image */}
        {canUseReference && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useReferenceImage}
                  onChange={(e) => setUseReferenceImage(e.target.checked)}
                  className="rounded"
                />
                <Link2 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">Use start node image as style reference</span>
              </label>
            </div>

            {useReferenceImage && (
              <div className="space-y-3 mt-3">
                {/* Reference image preview */}
                <div className="flex items-center gap-3">
                  <img
                    src={referenceImageUrl}
                    alt="Reference"
                    className="h-16 w-auto rounded border object-cover"
                  />
                  <div className="text-xs text-blue-700">
                    <p className="font-medium">Style reference from start node</p>
                    <p className="text-blue-600">New image will match the style and aesthetic</p>
                  </div>
                </div>

                {/* Strength slider */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Style Matching Strength</Label>
                    <span className="text-xs text-blue-700 font-mono">{referenceStrength.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0.3"
                    max="0.9"
                    step="0.05"
                    value={referenceStrength}
                    onChange={(e) => setReferenceStrength(parseFloat(e.target.value))}
                    className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-blue-600">
                    <span>Loose style</span>
                    <span>Strict match</span>
                  </div>
                </div>
              </div>
            )}

            {!useReferenceImage && (
              <p className="text-xs text-blue-600">
                Enable this to generate new images that match the style of your start node.
              </p>
            )}
          </div>
        )}

        {/* Info for start node */}
        {isStartNode && (
          <div className="mt-3 p-2 bg-green-50 rounded text-xs text-green-700">
            <p className="font-medium">This is the start node</p>
            <p>Its image will be available as a style reference for other nodes.</p>
          </div>
        )}

        {/* Parent Context Settings - Only show for non-start nodes with parents */}
        {hasParentContext && (
          <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useParentContext}
                onChange={(e) => setUseParentContext(e.target.checked)}
                className="rounded"
              />
              <GitBranch className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium">Include previous scene context</span>
            </label>

            {useParentContext && (
              <div className="mt-2 text-xs text-purple-700 bg-purple-100/50 p-2 rounded">
                <p className="font-medium mb-1">Context from parent scene(s):</p>
                {parentNodes.map((p, i) => (
                  <p key={p.id} className="italic truncate">
                    {p.title || 'Previous Scene'}: {(p.generated_prompt || p.situation_text)?.slice(0, 80)}...
                  </p>
                ))}
              </div>
            )}

            {!useParentContext && (
              <p className="mt-1 text-xs text-purple-600">
                Enable this to help Claude generate images that continue the story from the previous scene.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Node Type Toggles */}
      <div className="flex items-center gap-6 border-t pt-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_start}
            onChange={(e) => setFormData({ ...formData, is_start: e.target.checked })}
            className="rounded"
          />
          <Play className="h-4 w-4 text-green-600" />
          <span className="text-sm">Start Node</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_ending}
            onChange={(e) => setFormData({ ...formData, is_ending: e.target.checked, choices: e.target.checked ? [] : formData.choices })}
            className="rounded"
          />
          <Flag className="h-4 w-4 text-red-600" />
          <span className="text-sm">Ending Node</span>
        </label>
      </div>

      {/* Ending Options (only if is_ending) */}
      {formData.is_ending && (
        <div className="space-y-3 bg-red-50 p-3 rounded-lg">
          <div>
            <Label>Ending Type</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {OUTCOME_TYPES.map(ot => (
                <button
                  key={ot.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, ending_type: ot.value })}
                  className={`px-3 py-1 text-sm rounded-full border ${
                    formData.ending_type === ot.value
                      ? `${ot.color} text-white border-transparent`
                      : 'bg-background border-gray-200'
                  }`}
                >
                  {ot.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="ending_message">Ending Message</Label>
            <textarea
              id="ending_message"
              className="w-full px-3 py-2 border rounded-md text-sm"
              rows={2}
              value={formData.ending_message}
              onChange={(e) => setFormData({ ...formData, ending_message: e.target.value })}
              placeholder="Summary shown at the end..."
            />
          </div>
        </div>
      )}

      {/* Choices (only if not ending) */}
      {!formData.is_ending && (
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between">
            <Label>Choices</Label>
            <Button type="button" variant="outline" size="sm" onClick={addChoice}>
              <Plus className="h-4 w-4 mr-1" /> Add Choice
            </Button>
          </div>

          {formData.choices.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No choices yet. Add choices to create branching paths.
            </p>
          ) : (
            <div className="space-y-3">
              {formData.choices.map((choice, index) => (
                <div key={choice.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-500 mt-2">{index + 1}.</span>
                    <div className="flex-1 space-y-2">
                      <Input
                        value={choice.text}
                        onChange={(e) => updateChoice(index, 'text', e.target.value)}
                        placeholder="Choice text..."
                      />
                      <div className="flex items-center gap-2">
                        <select
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
                          value={choice.target_node_id}
                          onChange={(e) => updateChoice(index, 'target_node_id', e.target.value)}
                        >
                          <option value="">-- Select target node --</option>
                          <option value="NEW">+ Create new node</option>
                          {availableTargets.map(n => (
                            <option key={n.id} value={n.id}>
                              {n.title || n.situation_text?.slice(0, 40) + '...' || 'Untitled'}
                              {n.is_ending ? ' [END]' : ''}
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={choice.is_optimal}
                            onChange={(e) => updateChoice(index, 'is_optimal', e.target.checked)}
                            className="rounded"
                          />
                          <Target className={`h-4 w-4 ${choice.is_optimal ? 'text-green-600' : 'text-gray-400'}`} />
                        </label>
                      </div>
                      <Input
                        value={choice.feedback || ''}
                        onChange={(e) => updateChoice(index, 'feedback', e.target.value)}
                        placeholder="Feedback after selection (optional)"
                        className="text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Points modifier:</Label>
                        <Input
                          type="number"
                          value={choice.points_modifier}
                          onChange={(e) => updateChoice(index, 'points_modifier', parseInt(e.target.value) || 0)}
                          className="w-20"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeChoice(index)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {node?.id && onDelete && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onDelete(node)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete Node
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button type="submit" disabled={saving || !formData.situation_text}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};

// ============================================
// Tree Node Component
// ============================================
const TreeNode = ({ node, allNodes, level, onEdit, onAddChild, isSelected }) => {
  const [expanded, setExpanded] = useState(level < 2);

  // Find child nodes (nodes that this node links to)
  const childNodeIds = node.choices?.map(c => c.target_node_id).filter(Boolean) || [];
  const childNodes = allNodes.filter(n => childNodeIds.includes(n.id));
  const hasChildren = childNodes.length > 0;

  const getNodeIcon = () => {
    if (node.is_start) return <Play className="h-4 w-4 text-green-600" />;
    if (node.is_ending) return <Flag className="h-4 w-4 text-red-600" />;
    return <GitBranch className="h-4 w-4 text-blue-600" />;
  };

  const getEndingBadge = () => {
    if (!node.is_ending) return null;
    const outcome = OUTCOME_TYPES.find(o => o.value === node.ending_type);
    return (
      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full text-white ${outcome?.color || 'bg-gray-500'}`}>
        {outcome?.label || 'End'}
      </span>
    );
  };

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${
          isSelected ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700' : ''
        }`}
        onClick={() => onEdit(node)}
      >
        {/* Expand/collapse toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className={`p-0.5 hover:bg-gray-200 rounded ${!hasChildren ? 'invisible' : ''}`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
        </button>

        {/* Node icon */}
        {getNodeIcon()}

        {/* Node title/text */}
        <span className="flex-1 text-sm truncate">
          {node.title || node.situation_text?.slice(0, 50) || 'Untitled'}
        </span>

        {/* Image indicator */}
        {node.image_url ? (
          <ImageIcon className="h-4 w-4 text-green-600 flex-shrink-0" title="Has image" />
        ) : (
          <ImageIcon className="h-4 w-4 text-gray-300 flex-shrink-0" title="No image" />
        )}

        {/* Ending badge */}
        {getEndingBadge()}

        {/* Choice count */}
        {!node.is_ending && (
          <span className="text-xs text-gray-400">
            {node.choices?.length || 0} choices
          </span>
        )}

        {/* Edit button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onEdit(node); }}
          className="opacity-0 group-hover:opacity-100"
        >
          <Edit2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div className="ml-6 border-l border-gray-200 pl-2 space-y-1">
          {childNodes.map(childNode => (
            <TreeNode
              key={childNode.id}
              node={childNode}
              allNodes={allNodes}
              level={level + 1}
              onEdit={onEdit}
              onAddChild={onAddChild}
              isSelected={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Main ScenarioBuilder Component
// ============================================
const ScenarioBuilder = ({ scenario, onBack }) => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadNodes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/scenarios/${scenario.id}/nodes`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setNodes(data);
      }
    } catch (error) {
      console.error('Failed to load nodes:', error);
    } finally {
      setLoading(false);
    }
  }, [scenario.id]);

  useEffect(() => {
    loadNodes();
  }, [loadNodes]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/scenarios/${scenario.id}/nodes/bulk`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nodes })
      });

      if (response.ok) {
        setHasUnsavedChanges(false);
        // Reload to get any server-side updates
        await loadNodes();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save nodes');
      }
    } catch (error) {
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNode = () => {
    setEditingNode(null);
    setShowNodeEditor(true);
  };

  const handleEditNode = (node) => {
    setEditingNode(node);
    setShowNodeEditor(true);
  };

  const handleSaveNode = async (formData) => {
    if (editingNode) {
      // Update existing node
      const updated = nodes.map(n =>
        n.id === editingNode.id
          ? { ...n, ...formData, updated_at: new Date().toISOString() }
          : n
      );

      // If this is now the start node, unmark others
      if (formData.is_start) {
        updated.forEach(n => {
          if (n.id !== editingNode.id) n.is_start = false;
        });
      }

      // Handle "NEW" target nodes - create placeholder nodes
      const newNodes = [];
      formData.choices?.forEach(choice => {
        if (choice.target_node_id === 'NEW') {
          const newNode = {
            id: generateId(),
            scenario_id: scenario.id,
            title: '',
            situation_text: 'New scene - click to edit',
            choices: [],
            is_start: false,
            is_ending: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          newNodes.push(newNode);
          choice.target_node_id = newNode.id;
        }
      });

      setNodes([...updated, ...newNodes]);
    } else {
      // Create new node
      const newNode = {
        id: generateId(),
        scenario_id: scenario.id,
        ...formData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // If first node, make it start
      if (nodes.length === 0) {
        newNode.is_start = true;
      }

      // If this is the start node, unmark others
      if (formData.is_start) {
        const updated = nodes.map(n => ({ ...n, is_start: false }));
        setNodes([...updated, newNode]);
      } else {
        setNodes([...nodes, newNode]);
      }
    }

    setHasUnsavedChanges(true);
    setShowNodeEditor(false);
    setEditingNode(null);
  };

  const handleDeleteNode = async (node) => {
    if (!window.confirm('Delete this node? Links to it will be broken.')) return;

    setNodes(nodes.filter(n => n.id !== node.id));
    setHasUnsavedChanges(true);
    setShowNodeEditor(false);
    setEditingNode(null);
  };

  // Find the start node
  const startNode = nodes.find(n => n.is_start);

  // Find orphan nodes (not linked from any other node and not start)
  const linkedNodeIds = new Set();
  nodes.forEach(n => {
    n.choices?.forEach(c => {
      if (c.target_node_id) linkedNodeIds.add(c.target_node_id);
    });
  });
  const orphanNodes = nodes.filter(n => !n.is_start && !linkedNodeIds.has(n.id));

  // Validation warnings
  const warnings = [];
  if (!startNode) warnings.push('No start node defined');
  if (!nodes.some(n => n.is_ending)) warnings.push('No ending nodes defined');
  if (orphanNodes.length > 0) warnings.push(`${orphanNodes.length} orphan node(s) not reachable`);
  nodes.forEach(n => {
    if (!n.is_ending && (!n.choices || n.choices.length === 0)) {
      warnings.push(`Node "${n.title || 'Untitled'}" has no choices and is not an ending`);
    }
    n.choices?.forEach(c => {
      if (!c.target_node_id || c.target_node_id === '') {
        warnings.push(`Choice "${c.text || 'Untitled'}" has no target`);
      }
    });
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="text-2xl">{scenario.icon_emoji}</span>
              {scenario.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {nodes.length} nodes | {nodes.filter(n => n.is_ending).length} endings
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleAddNode}>
            <Plus className="h-4 w-4 mr-1" /> Add Node
          </Button>
          <Button
            onClick={handleSaveAll}
            disabled={saving || !hasUnsavedChanges}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save All
            {hasUnsavedChanges && <span className="ml-1 text-yellow-300">*</span>}
          </Button>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Validation Warnings</p>
              <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                {warnings.slice(0, 5).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
                {warnings.length > 5 && (
                  <li>...and {warnings.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : nodes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No nodes yet. Create your first node to start building the scenario.</p>
            <Button onClick={handleAddNode}>
              <Plus className="h-4 w-4 mr-1" /> Create Start Node
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Tree View */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Scenario Tree</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto space-y-1">
              {startNode && (
                <TreeNode
                  node={startNode}
                  allNodes={nodes}
                  level={0}
                  onEdit={handleEditNode}
                  onAddChild={handleAddNode}
                />
              )}

              {/* Orphan nodes section */}
              {orphanNodes.length > 0 && (
                <div className="mt-4 pt-4 border-t border-dashed">
                  <p className="text-xs text-orange-600 font-medium mb-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Orphan Nodes (not linked)
                  </p>
                  {orphanNodes.map(node => (
                    <TreeNode
                      key={node.id}
                      node={node}
                      allNodes={nodes}
                      level={0}
                      onEdit={handleEditNode}
                      onAddChild={handleAddNode}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Nodes List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">All Nodes ({nodes.length})</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              <div className="space-y-2">
                {nodes.map(node => (
                  <div
                    key={node.id}
                    className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleEditNode(node)}
                  >
                    {node.is_start && <Play className="h-4 w-4 text-green-600 flex-shrink-0" />}
                    {node.is_ending && <Flag className="h-4 w-4 text-red-600 flex-shrink-0" />}
                    {!node.is_start && !node.is_ending && <GitBranch className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                    <span className="text-sm flex-1 truncate">
                      {node.title || node.situation_text?.slice(0, 40) || 'Untitled'}
                    </span>
                    {node.image_url ? (
                      <ImageIcon className="h-4 w-4 text-green-600 flex-shrink-0" title="Has image" />
                    ) : (
                      <ImageIcon className="h-4 w-4 text-gray-300 flex-shrink-0" title="No image" />
                    )}
                    <span className="text-xs text-gray-400">
                      {node.choices?.length || 0}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Node Editor Modal */}
      <Dialog open={showNodeEditor} onOpenChange={(open) => { setShowNodeEditor(open); if (!open) setEditingNode(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingNode ? 'Edit Node' : 'New Node'}
            </DialogTitle>
          </DialogHeader>
          <NodeEditor
            node={editingNode}
            allNodes={nodes}
            scenarioId={scenario.id}
            onSave={handleSaveNode}
            onCancel={() => { setShowNodeEditor(false); setEditingNode(null); }}
            onDelete={editingNode ? handleDeleteNode : null}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScenarioBuilder;
